import { API_BASE_URL } from "../../../../config/apiConfig";
// services/flightService.js
import axios from 'axios';
import { useContext } from 'react';
import { AuthContext } from '../../../../Context/AuthContext';

/* ======================================================
   AXIOS BACKEND CLIENT
====================================================== */
const createApiClient = (token = null) => {
  const client = axios.create({
    baseURL:  API_BASE_URL,
    timeout: 30000,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  });

  client.interceptors.request.use((config) => {
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      console.error('Flight API Error:', error.response?.status, error.response?.data);
      return Promise.reject(error);
    }
  );

  return client;
};

/* ======================================================
   CACHE UTIL (TTL)
====================================================== */
const CACHE_TTL = 1000 * 60 * 60 * 24 * 7; // 7 days for flight searches

const cacheGet = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const { value, expires } = JSON.parse(raw);
    if (Date.now() > expires) {
      localStorage.removeItem(key);
      return null;
    }
    return value;
  } catch {
    return null;
  }
};

const cacheSet = (key, value, ttl = CACHE_TTL) => {
  localStorage.setItem(
    key,
    JSON.stringify({
      value,
      expires: Date.now() + ttl
    })
  );
};

/* ======================================================
   HOOK VERSION OF FLIGHT SERVICE
====================================================== */
export const useFlightService = () => {
  const { token } = useContext(AuthContext);
  
  if (!token) {
    console.warn('No auth token found in useFlightService');
  }

  const client = createApiClient(token);
  return createFlightService(client);
};

/* ======================================================
   CORE FLIGHT SERVICE FACTORY
====================================================== */
const createFlightService = (client) => ({
  /* ======================================================
     SEARCH FLIGHTS (MAIN API)
  ====================================================== */
 // In FlightService.js - update the searchFlights function:
async searchFlights(params) {

  //console.log('🔍 searchFlights called with params:', params);
  try {
    const {
      departureCode,
      destinationCode,
      departureDate,
      returnDate,
      adults = 1,
      children = 0,
      infants = 0,
      tripType = 'roundTrip',
      currency = 'USD'
    } = params;

    // Validate required parameters
    if (!departureCode || !destinationCode || !departureDate) {
      throw new Error('Departure code, destination code, and departure date are required');
    }

    // Extract airport codes (handle both full strings and codes)
    const extractAirportCode = (input) => {
      if (!input) return '';
      
      // If it's already a 3-letter code, return it uppercase
      if (typeof input === 'string' && input.length === 3 && /^[A-Z]{3}$/.test(input.toUpperCase())) {
        return input.toUpperCase();
      }
      
      // Try to extract code from string like "DUBAI (DXB) - INTERNATIONAL"
      const match = input.match(/\(([A-Z]{3})\)/);
      if (match && match[1]) {
        return match[1];
      }
      
      // Return original if no pattern matched
      return input.toUpperCase();
    };

    

    const departureAirportCode = extractAirportCode(departureCode);
    const destinationAirportCode = extractAirportCode(destinationCode);

    // Validate extracted codes
    if (!departureAirportCode || departureAirportCode.length !== 3) {
      throw new Error('Invalid departure airport code. Please select a valid airport.');
    }
    
    if (!destinationAirportCode || destinationAirportCode.length !== 3) {
      throw new Error('Invalid destination airport code. Please select a valid airport.');
    }

    // Create cache key based on search parameters
    const cacheKey = `flight-search:${departureAirportCode}:${destinationAirportCode}:${departureDate}:${returnDate || 'oneway'}:${adults}:${children}:${infants}:${tripType}:${currency}`;
    
    // Check cache
    const cachedResults = cacheGet(cacheKey);
    if (cachedResults) {
      //console.log('Returning cached flight results');
      return cachedResults;
    }

    const payload = {
      departureCode: departureAirportCode, // Use extracted 3-letter code
      destinationCode: destinationAirportCode, // Use extracted 3-letter code
      departureDate: this.formatDateForAPI(departureDate),
      returnDate: returnDate ? this.formatDateForAPI(returnDate) : null,
      adults: Math.max(1, Math.min(9, adults)),
      children: children ? Math.max(0, Math.min(8, children)) : 0,
      infants: infants ? Math.max(0, Math.min(8, infants)) : 0,
      tripType: tripType ,
      currency: currency || 'USD'
    };

    //console.log('Sending flight search payload:', payload);
    
    const response = await client.post('/discover/flights/search', payload);
    
    if (!response.data) {
      throw new Error('No data returned from server');
    }

    const data = response.data || [];
    
    // Cache the results
    cacheSet(cacheKey, data);
    
    return data;
  } catch (error) {
    console.error('Error in searchFlights:', error);
    
    if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    }
    
    if (error.response?.status === 422) {
      const errorMessage = error.response?.data?.error || 'Invalid search parameters. Please check your inputs.';
      throw new Error(errorMessage);
    }
    
    if (error.response?.status === 500) {
      const errorMessage = error.response?.data?.error || 'Server error. Please try again later.';
      throw new Error(errorMessage);
    }
    
    throw error;
  }
},

  /* ======================================================
     GET AIRPORT SUGGESTIONS
  ====================================================== */
  async getAirportSuggestions(query) {
    try {
      if (!query || query.length < 2) {
        return [];
      }

      const cacheKey = `airport-suggestions:${query.toLowerCase()}`;
      const cached = cacheGet(cacheKey);
      if (cached) return cached;

      const response = await client.post('/discover/airports/search', {
        query: query
      });

      const suggestions = (response.data || []).map(airport => ({
        code: airport.code || airport.iata || '',
        name: airport.name || '',
        city: airport.city || '',
        country: airport.country || '',
        countryCode: airport.countryCode || ''
      }));

      cacheSet(cacheKey, suggestions);
      return suggestions;
    } catch (error) {
      console.error('Error in getAirportSuggestions:', error);
      return [];
    }
  },

  /* ======================================================
     GET FLIGHT DETAILS
  ====================================================== */
  async getFlightDetails(flightId) {
    try {
      if (!flightId) {
        throw new Error('Flight ID is required');
      }

      const cacheKey = `flight-details:${flightId}`;
      const cached = cacheGet(cacheKey);
      if (cached) return cached;

      const response = await client.get(`/discover/flights/${flightId}`);
      
      cacheSet(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error('Error in getFlightDetails:', error);
      throw error;
    }
  },

  /* ======================================================
     GET CHEAPEST FLIGHTS
  ====================================================== */
  async getCheapestFlights(params) {
    try {
      const { departureCode, destinationCode, month, year, currency = 'USD' } = params;
      
      if (!departureCode || !destinationCode || !month || !year) {
        throw new Error('All parameters are required');
      }

      const cacheKey = `cheapest-flights:${departureCode}:${destinationCode}:${month}:${year}:${currency}`;
      const cached = cacheGet(cacheKey);
      if (cached) return cached;

      const response = await client.post('/discover/flights/cheapest', {
        departureCode: departureCode.toUpperCase(),
        destinationCode: destinationCode.toUpperCase(),
        month: parseInt(month),
        year: parseInt(year),
        currency: currency
      });

      cacheSet(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error('Error in getCheapestFlights:', error);
      throw error;
    }
  },

  /* ======================================================
     HELPER FUNCTIONS
  ====================================================== */
  formatDateForAPI(date) {
    // Accepts Date object or string
    const d = new Date(date);
    return d.toISOString().split('T')[0]; // YYYY-MM-DD
  },

  formatDateForDisplay(date) {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  },

  calculateTotalPassengers(adults, children, infants) {
    return adults + children + infants;
  }
});

/* ======================================================
   DEFAULT EXPORT
====================================================== */
const getTokenFromStorage = () => {
  try {
    return localStorage.getItem('token');
  } catch {
    return null;
  }
};

const FlightService = createFlightService(
  createApiClient(getTokenFromStorage())
);

export default FlightService;